import React, { useEffect } from 'react';
import clsx from 'clsx';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import TempoCard, { CompletedPill } from 'components/common/TempoCard';
import RTMCard from 'components/common/RTMCard';
import Spinner from 'components/spinner/Spinner';
import useChoiceScreen from 'components/communiqueByPatientInfo/useChoiceScreen';
import useIntakeFlags from 'hooks/useIntakeFlags';
import useIncompleteRtmRedirect from 'hooks/useIncompleteRtmRedirect';
import useIncompleteTempoRedirect from 'hooks/useIncompleteTempoRedirect';

type Props = {
  hasRachelIntakeIntegration: boolean;
  onAiCallSuccess: (doneNote: string) => void;
  onFinishLater: () => void;
};

type ProgressStepsProps = {
  currentStepNumber: number;
  totalSteps: number;
  label?: string;
};

type CallingScreenProps = {
  onFillItOutInstead: () => void;
  onFinishLater: () => void;
};

const choiceTileBaseClass = 'flex min-h-[112px] flex-col items-center justify-center rounded-2xl border border-populate-green/15 bg-white px-5 py-4 text-center text-gray-900 shadow-[0_10px_28px_rgba(21,184,166,0.10)] transition hover:bg-[#f8fffd] hover:shadow-[0_14px_32px_rgba(21,184,166,0.14)] disabled:cursor-not-allowed disabled:opacity-60';

function getProgressBarClassName(index: number, currentStepNumber: number) {
  if (index < currentStepNumber - 1) {
    return 'bg-populate-green';
  }

  if (index === currentStepNumber - 1) {
    return 'bg-populate-green opacity-50';
  }

  return 'bg-gray-200';
}

function CallingPhoneIcon() {
  return (
    <div className="relative mx-auto my-2 flex h-28 w-28 items-center justify-center">
      {[0, 0.6, 1.2].map((delay) => (
        <span
          key={delay}
          aria-hidden
          className="pointer-events-none absolute h-20 w-20 rounded-full"
          style={{
            backgroundColor: 'rgba(21, 184, 166, 0.45)',
            animation: 'calling-phone-ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite',
            animationDelay: `${delay}s`,
            transformOrigin: 'center',
          }}
        />
      ))}

      <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-populate-green text-white shadow-[0_14px_34px_rgba(21,184,166,0.28)]">
        <PhoneInTalkRoundedIcon fontSize="large" />
      </div>

      <style>
        {`
    @keyframes calling-phone-ping {
      0%   { transform: scale(1);   opacity: 0.45; }
      100% { transform: scale(1.7); opacity: 0;    }
    }
  `}
      </style>
    </div>
  );
}

export function ProgressSteps({
  currentStepNumber,
  totalSteps,
  label,
}: ProgressStepsProps) {
  return (
    <div className="flex items-center gap-3">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div
          key={`progress-${index + 1}`}
          className={clsx(
            'h-1.5 flex-1 rounded-full',
            getProgressBarClassName(index, currentStepNumber),
          )}
        />
      ))}
      <span className="text-sm font-medium text-gray-500">
        {label ?? `${currentStepNumber} of ${totalSteps}`}
      </span>
    </div>
  );
}

export function CallingScreen({
  onFillItOutInstead,
  onFinishLater,
}: CallingScreenProps) {
  const { flags, pccItems } = useIntakeFlags();
  const { hasIncompleteTempo, startTempo } = useIncompleteTempoRedirect(flags, pccItems);
  const {
    hasIncompleteRtm,
    rtmCardCopy,
    startRtm,
  } = useIncompleteRtmRedirect(flags, pccItems);
  const showTempoCard = hasIncompleteTempo;
  const showRtmCard = hasIncompleteRtm && Boolean(rtmCardCopy);
  const totalSteps = 2
    + Number(hasIncompleteTempo)
    + Number(hasIncompleteRtm);
  const currentStepNumber = 2;
  const finishLaterLabel = hasIncompleteTempo || hasIncompleteRtm
    ? "I'll finish this later"
    : "I'm done";

  return (
    <div className="flex w-full flex-col items-center justify-center gap-6 text-center">
      <div className="w-full">
        <ProgressSteps
          currentStepNumber={currentStepNumber}
          totalSteps={totalSteps}
          label="In progress"
        />

        <div className="mt-6 flex items-center justify-center gap-2 text-populate-green">
          <CheckCircleOutlineRoundedIcon fontSize="small" />
          <span className="text-sm font-semibold">Checked in</span>
        </div>

        <CallingPhoneIcon />
        <h2 className="text-2xl font-bold tracking-[-0.02em] text-gray-900">
          We&apos;re calling you now
        </h2>
        <p className="mx-auto mt-3 max-w-[360px] text-base text-gray-600">
          Please answer your phone — our agent will collect your intake.
        </p>
        <button
          type="button"
          className="mt-4 text-base font-semibold text-populate-green transition hover:text-populate-deep-green"
          onClick={onFillItOutInstead}
        >
          Didn&apos;t get a call? Fill it out instead →
        </button>

        {(showTempoCard || showRtmCard) && (
          <div className="mt-8 w-full">
            <div className="mb-4 flex items-center gap-3 px-1">
              <div className="h-px flex-1 bg-gray-200" />
              <div className="text-center text-sm text-gray-500">
                After your call
              </div>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <div
              className={clsx(
                'grid gap-3 items-stretch',
                showTempoCard && showRtmCard && 'sm:grid-cols-2',
              )}
            >
              {showTempoCard && (
                <TempoCard onStart={startTempo} subtitle="Questionnaire • about 3 min" />
              )}
              {showRtmCard && rtmCardCopy ? (
                <RTMCard
                  icon={rtmCardCopy.icon}
                  title={rtmCardCopy.title}
                  subtitle={rtmCardCopy.subtitle}
                  startLabel={rtmCardCopy.startLabel}
                  onStart={startRtm}
                />
              ) : null}
            </div>
          </div>
        )}

        <button
          type="button"
          className="mt-8 text-base font-medium text-gray-500 transition hover:text-gray-700"
          onClick={onFinishLater}
        >
          {finishLaterLabel}
        </button>
      </div>
    </div>
  );
}

export default function ChoiceScreen({
  hasRachelIntakeIntegration,
  onAiCallSuccess,
  onFinishLater,
}: Props) {
  const {
    currentStepNumber,
    errorMessage,
    finishLaterLabel,
    handleCallMe,
    handleStartIntake,
    hasRtmCard,
    hasTempoCard,
    intakeButtonLabel,
    intakeStep,
    isAllStepsComplete,
    isCalling,
    isLoading,
    isRtmComplete,
    isTempoComplete,
    remainingStepsLabel,
    rtmCardCopy,
    rtmStartLabel,
    shouldShowIntake,
    startRtm,
    startTempo,
    tempoStartLabel,
    tempoStep,
    totalSteps,
  } = useChoiceScreen({
    onAiCallSuccess,
  });

  const showTempoCard = hasTempoCard && Boolean(tempoStep);
  const showRtmCard = hasRtmCard;

  useEffect(() => {
    let redirectTimeout: ReturnType<typeof setTimeout> | undefined;

    if (!isLoading && isAllStepsComplete) {
      redirectTimeout = setTimeout(onFinishLater, 5000);
    }

    return () => clearTimeout(redirectTimeout);
  }, [isAllStepsComplete, isLoading, onFinishLater]);

  return (
    <div className="flex w-full flex-col items-center justify-center gap-6 text-center">
      {isLoading && (
        <div className="flex min-h-[320px] w-full items-center justify-center px-6 py-8">
          <Spinner />
        </div>
      )}

      {!isLoading && (
        <div className="w-full">
          <ProgressSteps
            currentStepNumber={currentStepNumber}
            totalSteps={totalSteps}
          />

          {errorMessage && (
            <p className="error-message mb-4 text-center">{errorMessage}</p>
          )}

          <div className="mt-6 flex items-center gap-2 text-populate-green">
            <CheckCircleOutlineRoundedIcon fontSize="small" />
            <span className="text-sm font-semibold">Checked in</span>
          </div>

          <h2 className="mt-2 text-left text-2xl font-bold tracking-[-0.02em] text-gray-900">
            {remainingStepsLabel}
          </h2>
          <p className="mt-2 max-w-[420px] text-left text-base text-gray-600">
            Finishing now means the doctor has everything needed
            before you&apos;re seen.
          </p>

          {intakeStep && (
            <div className="mt-6 rounded-3xl border border-populate-green/15 bg-[linear-gradient(180deg,rgba(21,184,166,0.08),rgba(255,255,255,0.96))] p-4 shadow-sm">
              <div className="text-left">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-bold tracking-[-0.02em] text-gray-900">
                    {intakeStep.title}
                  </h3>
                  {!shouldShowIntake && <CompletedPill />}
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  {shouldShowIntake && intakeStep.subtitle}
                </p>
              </div>

              {shouldShowIntake && (
                <div
                  className={clsx(
                    'mt-5 grid gap-3',
                    hasRachelIntakeIntegration
                      ? 'grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center'
                      : 'grid-cols-1',
                  )}
                >
                  <button
                    type="button"
                    className={choiceTileBaseClass}
                    onClick={handleStartIntake}
                  >
                    <DescriptionRoundedIcon className="!text-populate-green" fontSize="small" />
                    <div className="mt-3 flex flex-col items-center">
                      <p className="text-lg font-bold tracking-[-0.01em]">{intakeButtonLabel}</p>
                      <p className="mt-1 text-sm text-gray-600">Digital form</p>
                    </div>
                  </button>

                  {hasRachelIntakeIntegration && (
                    <>
                      <div className="text-center text-sm font-medium text-gray-400">or</div>
                      <button
                        type="button"
                        className={choiceTileBaseClass}
                        onClick={handleCallMe}
                        disabled={isCalling}
                      >
                        <PhoneInTalkRoundedIcon
                          className="!text-populate-green"
                          fontSize="small"
                        />
                        <div className="mt-3 flex flex-col items-center">
                          <p className="text-lg font-bold tracking-[-0.01em]">
                            {isCalling ? 'Calling...' : 'Talk it through'}
                          </p>
                          <p className="mt-1 text-sm text-gray-600">Quick AI call</p>
                        </div>
                      </button>
                    </>
                  )}
                </div>
              )}

            </div>
          )}

          {(showTempoCard || showRtmCard) && (
            <div
              className={clsx(
                'mt-6 grid gap-3 items-stretch',
                showTempoCard && showRtmCard && 'sm:grid-cols-2',
              )}
            >
              {showTempoCard && tempoStep && (
                <TempoCard
                  isCompleted={isTempoComplete}
                  onStart={startTempo}
                  startLabel={tempoStartLabel}
                  subtitle={tempoStep.subtitle}
                  title={tempoStep.title}
                />
              )}
              {showRtmCard && rtmCardCopy ? (
                <RTMCard
                  icon={rtmCardCopy.icon}
                  title={rtmCardCopy.title}
                  subtitle={rtmCardCopy.subtitle}
                  startLabel={rtmStartLabel}
                  isCompleted={isRtmComplete}
                  onStart={startRtm}
                />
              ) : null}
            </div>
          )}

          <button
            type="button"
            className="mt-6 text-base font-medium text-gray-500 transition hover:text-gray-700"
            onClick={onFinishLater}
          >
            {finishLaterLabel}
          </button>
        </div>
      )}
    </div>
  );
}
